/**
 * Vulnerability Management Dashboard
 *
 * Shows overview stat cards and expandable projects table.
 */
import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layout';
import StatCard from '../../components/ui/StatCard/StatCard';
import ProjectsTable from './SbomAnalysisTable';
import CreateProjectModal from './CreateProjectModal';
import UploadScanModal from './UploadScanModal';
import { useVulnerabilityManagement } from '../../contexts/VulnerabilityManagementContext';
import { useConfirm } from '../../components/ui/AlertDialog';
import { Icon } from '../../components/ui/Icon';
import styles from '../../styles/dashboardLayout.module.css';
import localStyles from './VulnerabilityDashboard.module.css';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Per-stat accent colour. Tokens where the semantic exists
 * (primary-blue, status-warning, status-success); the purple
 * "Scans" tile keeps a literal hex — there's no semantic
 * neutral-accent token and the value is intentionally distinct
 * from the three status tones (success / warning / error).
 */
const STAT_COLORS = {
  projects: 'var(--color-primary-blue)',
  scans: '#8B5CF6',
  vulns: 'var(--color-status-warning)',
  components: 'var(--color-status-success)',
};

const VulnerabilityDashboard = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploadingForProject, setUploadingForProject] = useState(null);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const { confirm, confirmDialog } = useConfirm();

  // Changing page size resets to page 1 so the server-fetched window
  // starts fresh — matches the Athena dashboard ProjectsTable's
  // setItemsPerPage handler.
  const handlePageSizeChange = (next) => {
    setPageSize(next);
    setCurrentPage(1);
  };

  const {
    overview,
    projects,
    projectsTotal,
    loadingOverview,
    loadingProjects,
    fetchOverview,
    fetchProjects,
    deleteProject,
    deleteScan,
    setScanTracked,
    error
  } = useVulnerabilityManagement();

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchProjects(currentPage, pageSize);
  }, [fetchProjects, currentPage, pageSize]);

  const handleNewProject = () => {
    setShowCreateModal(true);
  };

  const handleProjectCreated = () => {
    // Refresh both the overview counters and the projects table
    fetchOverview();
    fetchProjects(currentPage, pageSize);
  };

  const handleUploadScan = (projectUuid) => {
    setUploadingForProject(projectUuid);
  };

  const handleScanUploaded = () => {
    // Refresh the projects list + the overview counters so the new
    // scan + its component/vulnerability counts show up immediately.
    fetchOverview();
    fetchProjects(currentPage, pageSize);
  };

  const handleDeleteProject = async (uuid) => {
    const ok = await confirm({
      title: 'Delete project?',
      description: 'This permanently deletes the project and all its scans, and cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await deleteProject(uuid);
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  };

  const handleVersionClick = (scanUuid, isLatest, versions) => {
    navigate(`/athena/vulnerability-management/analysis/${scanUuid}`, { state: { isLatest, versions } });
  };

  const handleDeleteScan = async (uuid) => {
    const ok = await confirm({
      title: 'Delete scan?',
      description: 'This permanently deletes the scan and cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await deleteScan(uuid);
    } catch (err) {
      console.error('Failed to delete scan:', err);
    }
  };

  const handleToggleTracked = async (uuid, isLatest) => {
    setTrackingInProgress(prev => new Set(prev).add(uuid));
    try {
      await setScanTracked(uuid, isLatest);
    } catch (err) {
      console.error('Failed to toggle scan tracking:', err);
    } finally {
      setTrackingInProgress(prev => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  const getStatCards = () => {
    const isLoading = loadingOverview || !overview;
    return [
      {
        title: 'Projects',
        value: isLoading ? 0 : (overview.totalProjects || 0),
        subtitle: 'Created',
        icon: 'analyses',
        color: STAT_COLORS.projects,
        isLoading
      },
      {
        title: 'Scans',
        value: isLoading ? 0 : (overview.totalScans || 0),
        subtitle: 'Uploaded',
        icon: 'vulnerabilities',
        color: STAT_COLORS.scans,
        isLoading
      },
      {
        title: 'Vulns',
        value: isLoading ? 0 : (overview.vulnerabilities || 0),
        subtitle: 'Found',
        icon: 'critical',
        color: STAT_COLORS.vulns,
        isLoading
      },
      {
        title: 'Components',
        value: isLoading ? 0 : (overview.components || 0),
        subtitle: 'Analysed',
        icon: 'components',
        color: STAT_COLORS.components,
        isLoading
      }
    ];
  };

  return (
    <>
      <PageLayout
        activeNavItem="vulnerability-management"
        currentPage="Vulnerability Management"
        breadcrumbItems={[
          { label: 'Dashboard', path: '/athena' }
        ]}
        contentClassName={localStyles.contentAreaFullWidth}
        disableScroll
      >
        <div className={localStyles.dashboardContainer}>
        {error && (
          <div className={styles.errorBanner}>
            <span>Unable to load vulnerability data: {error}</span>
          </div>
        )}

        <section className={styles.statsSection}>
          <div className={styles.statsGrid} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {getStatCards().map((card, index) => (
              <StatCard key={card.title} {...card} index={index} />
            ))}
          </div>
        </section>

        <ProjectsTable
          projects={projects}
          total={projectsTotal}
          currentPage={currentPage}
          pageSize={pageSize}
          loading={loadingProjects}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          onNewProjectClick={handleNewProject}
          onUploadScanClick={handleUploadScan}
          onDeleteProjectClick={handleDeleteProject}
          onDeleteScanClick={handleDeleteScan}
          onToggleTracked={handleToggleTracked}
          trackingInProgress={trackingInProgress}
          onVersionClick={handleVersionClick}
        />
        </div>
      </PageLayout>

      {confirmDialog}

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleProjectCreated}
        />
      )}

      {uploadingForProject && (
        <UploadScanModal
          projectUuid={uploadingForProject}
          onClose={() => setUploadingForProject(null)}
          onUploaded={handleScanUploaded}
        />
      )}
    </>
  );
};

export default memo(VulnerabilityDashboard);
